import { getDietType, DietType } from './types'
import type { World } from './world'
import type { Organism } from './organism'

const CHART_W = 220
const CHART_H = 90
const CHART_MARGIN = 8

export class Renderer {
  private ctx: CanvasRenderingContext2D
  private width: number
  private height: number
  constructor(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas 2D context not available')
    this.ctx = ctx
    this.width = canvas.width
    this.height = canvas.height
  }

  resize(w: number, h: number): void {
    this.width = w
    this.height = h
  }

  render(world: World, selectedOrganism: Organism | null = null): void {
    const ctx = this.ctx

    // 背景
    ctx.fillStyle = '#0a0e1a'
    ctx.fillRect(0, 0, this.width, this.height)

    // 障害物（岩）
    for (const obs of world.obstacles) {
      ctx.beginPath()
      ctx.arc(obs.pos.x, obs.pos.y, obs.radius, 0, Math.PI * 2)
      ctx.fillStyle = '#3a3a4a'
      ctx.fill()
      ctx.strokeStyle = '#5a5a6a'
      ctx.lineWidth = 2
      ctx.stroke()
    }

    // 食料（四角形）
    for (const food of world.foods) {
      const fw = food.radius * 2
      ctx.fillStyle = '#3ddc84'
      ctx.shadowColor = '#3ddc84'
      ctx.shadowBlur = 6
      ctx.fillRect(food.pos.x - food.radius, food.pos.y - food.radius, fw, fw)
      ctx.shadowBlur = 0
    }

    // 生物
    for (const org of world.organisms) {
      const { traits, pos, energy } = org
      const energyRatio = energy / 150
      const isSelected = selectedOrganism !== null && org.id === selectedOrganism.id

      // 選択ハイライト
      if (isSelected) {
        ctx.beginPath()
        ctx.arc(pos.x, pos.y, traits.size + 6, 0, Math.PI * 2)
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)'
        ctx.lineWidth = 2
        ctx.stroke()
        // 視野範囲
        ctx.beginPath()
        ctx.arc(pos.x, pos.y, traits.visionRange, 0, Math.PI * 2)
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)'
        ctx.lineWidth = 1
        ctx.stroke()
      }

      // dietScoreからhueを動的算出: 肉食(0)→赤, 雑食(0.5)→青, 草食(1.0)→緑
      const ds = traits.dietScore
      const hue = ds <= 0.5 ? 360 - ds * 240 : 240 - (ds - 0.5) * 240
      // 世代が上がるほど彩度を高める（gen0: 50%, gen10+: 80%）
      const genBoost = Math.min(org.generation, 10) * 3

      // 本体
      ctx.beginPath()
      ctx.arc(pos.x, pos.y, traits.size, 0, Math.PI * 2)
      const saturation = 50 + genBoost + energyRatio * 10
      const lightness = 35 + energyRatio * 30
      ctx.fillStyle = `hsl(${hue}, ${saturation}%, ${lightness}%)`
      ctx.shadowColor = `hsl(${hue}, 100%, 60%)`
      ctx.shadowBlur = getDietType(traits.dietScore) === DietType.Carnivore ? 8 : 4
      ctx.fill()
      ctx.shadowBlur = 0

      ctx.beginPath()
      ctx.arc(pos.x, pos.y, traits.size, 0, Math.PI * 2)
      ctx.strokeStyle = `hsla(${hue}, 100%, 80%, 0.6)`
      ctx.lineWidth = 1
      ctx.stroke()

      // 速度ベクトル（速い個体のみ）
      if (traits.speed > 2.0) {
        const vlen = Math.sqrt(org.vel.x ** 2 + org.vel.y ** 2)
        if (vlen > 0.1) {
          const nx = org.vel.x / vlen
          const ny = org.vel.y / vlen
          ctx.beginPath()
          ctx.moveTo(pos.x, pos.y)
          ctx.lineTo(pos.x + nx * traits.size * 2, pos.y + ny * traits.size * 2)
          ctx.strokeStyle = `hsla(${hue}, 100%, 90%, 0.4)`
          ctx.lineWidth = 1
          ctx.stroke()
        }
      }

      // 世代バッジ（1世代以上で世代数を表示）
      if (org.generation >= 1) {
        ctx.fillStyle = 'rgba(255, 215, 0, 0.9)'
        ctx.font = `bold ${Math.max(7, Math.floor(traits.size * 0.85))}px monospace`
        ctx.textAlign = 'center'
        ctx.fillText(`${org.generation}`, pos.x, pos.y - traits.size - 2)
      }
    }

    // 個体数推移グラフ（右下）
    this.renderPopChart(world)
  }

  private renderPopChart(world: World): void {
    const history = world.popHistory
    if (history.length < 2) return

    const ctx = this.ctx
    const x0 = this.width - CHART_W - CHART_MARGIN
    const y0 = this.height - CHART_H - CHART_MARGIN

    // 背景
    ctx.fillStyle = 'rgba(10, 14, 26, 0.82)'
    ctx.strokeStyle = 'rgba(255,255,255,0.1)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.roundRect(x0, y0, CHART_W, CHART_H, 6)
    ctx.fill()
    ctx.stroke()

    // タイトル
    ctx.fillStyle = 'rgba(200,200,200,0.7)'
    ctx.font = '10px monospace'
    ctx.textAlign = 'left'
    ctx.fillText('\u500b\u4f53\u6570\u63a8\u79fb', x0 + 6, y0 + 12)

    const padL = 4
    const padR = 4
    const padT = 18
    const padB = 4
    const gx = x0 + padL
    const gy = y0 + padT
    const gw = CHART_W - padL - padR
    const gh = CHART_H - padT - padB

    // Y最大値
    let maxVal = 10
    for (const s of history) {
      const total = s.herbivores + s.carnivores + s.omnivores
      if (total > maxVal) maxVal = total
    }

    const toX = (i: number) => gx + (i / (history.length - 1)) * gw
    const toY = (v: number) => gy + gh - (v / maxVal) * gh

    const drawLine = (getter: (s: typeof history[0]) => number, color: string) => {
      ctx.beginPath()
      ctx.strokeStyle = color
      ctx.lineWidth = 1.5
      for (let i = 0; i < history.length; i++) {
        const px = toX(i)
        const py = toY(getter(history[i]))
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)
      }
      ctx.stroke()
    }

    drawLine(s => s.herbivores, '#6dbb6d')
    drawLine(s => s.carnivores, '#e05050')
    drawLine(s => s.omnivores, '#7070e0')

    // 凡例（右下）
    const lx = x0 + CHART_W - 68
    const ly = y0 + CHART_H - 36
    ctx.font = '9px monospace'
    ctx.textAlign = 'left'
    const legendItems: [string, string][] = [
      ['\u8349\u98df', '#6dbb6d'],
      ['\u8089\u98df', '#e05050'],
      ['\u96d1\u98df', '#7070e0'],
    ]
    legendItems.forEach(([label, color], i) => {
      const ry = ly + i * 12
      ctx.fillStyle = color
      ctx.fillRect(lx, ry, 9, 9)
      ctx.strokeStyle = 'rgba(255,255,255,0.5)'
      ctx.lineWidth = 0.5
      ctx.strokeRect(lx, ry, 9, 9)
      ctx.fillStyle = 'rgba(220,220,220,0.9)'
      ctx.fillText(label, lx + 12, ry + 8)
    })
  }
}
